import { useCallback, useMemo, useState } from "react"

import { useParams } from "react-router-dom"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { FilterListRoundedIcon } from "@l2r-front/l2r-icons"
import { useNetworkReferential, useNetworksDispatchContext, useRoleIsReadOnly } from "@l2r-front/l2r-networks"
import { useIsMobileDevice } from "@l2r-front/l2r-ui"
import { useNavigateWithSearchParams } from "@l2r-front/l2r-utils"

import { AddDocumentButton, AddDocumentButtonMobile } from "../../components/AddDocumentButton"
import { DocumentEditModal } from "../../components/DocumentEditModal"
import { DocumentLinearLocationSelector } from "../../components/DocumentLinearLocationSelector"
import { FILTERS } from "../../constants/filters"
import { I18N_NAMESPACE } from "../../constants/i18n"
import { MODULE_DOCUMENTS, NETWORK_ATTACHMENT, ROAD_ATTACHMENT } from "../../constants/modules"
import { ROAD_DOCUMENTS_PAGE } from "../../constants/urlParams"
import { DocumentAddForm } from "../../forms/DocumentAddForm"

import { DocumentRoadSidebarContent } from "../DocumentRoadSidebarContent"

import * as Styled from "./DocumentRoadSidebar.styled"

export function DocumentRoadSidebar() {

    const { t } = useTranslation([I18N_NAMESPACE])
    const navigate = useNavigateWithSearchParams()
    const { setRoadSearched } = useNetworksDispatchContext()
    const [displayFilters, setDisplayFilters] = useState(false)
    const isMobile = useIsMobileDevice()
    const readOnly = useRoleIsReadOnly()
    const [documentLocation, setDocumentLocation] = useState(null)
    const [linearLocationSelectorType, setLinearLocationSelectorType] = useState(null)
    const [documentAddModalType, setDocumentAddModalType] = useState(false)
    const { road } = useParams()
    const { data: referential } = useNetworkReferential()

    const closeFilters = () => setDisplayFilters(false)
    const toggleFilter = () => setDisplayFilters(displayFilters => !displayFilters)

    const closeLinearLocationModal = useCallback(() => {
        setLinearLocationSelectorType(null)
    }, [])

    const navigateBack = useCallback(() => {
        setRoadSearched(null)
        navigate(`../${MODULE_DOCUMENTS}`, { paramsToDelete: [ROAD_DOCUMENTS_PAGE] })
    }, [navigate, setRoadSearched])

    const handleAddDocumentMenuSelect = useCallback((selectedType) => {
        if (selectedType === NETWORK_ATTACHMENT) {
            setDocumentAddModalType(selectedType)
        } else if (selectedType.toLowerCase() === ROAD_ATTACHMENT) {
            setDocumentAddModalType(ROAD_ATTACHMENT)
            setDocumentLocation(getReferentialRoad(referential, road))
        } else {
            setLinearLocationSelectorType(selectedType)
        }
    }, [referential, road])

    const handleSelectLinearLocation = useCallback((location) => {
        setDocumentLocation(location)
        if (location) {
            setDocumentAddModalType(linearLocationSelectorType)
        }
        setLinearLocationSelectorType(null)
    }, [linearLocationSelectorType])

    const DesktopFilterComponent = useMemo(() => <Styled.DisplayedFiltersWrapper>
        <Styled.IconButton onClick={toggleFilter} id="filters-button">
            <FilterListRoundedIcon color="cta-bg/cta-bg-primary" />
        </Styled.IconButton>
    </Styled.DisplayedFiltersWrapper>, [])

    return (
        <Styled.BottomSheetContent
            id="documentRoad-sidebar"
            title={t(I18N_NAMESPACE, "containers.documentRoadSidebar.title")}
            titleBottomComponent={!isMobile ? <Styled.DocumentFilterList enabledFilters={FILTERS} /> : null}
            titleSideComponent={<Styled.SideComponentContainer>
                {
                    !isMobile && DesktopFilterComponent
                }
                {!readOnly && !isMobile && <AddDocumentButton onAddDocumentMenuSelected={handleAddDocumentMenuSelect} />}
                {!readOnly && isMobile && <AddDocumentButtonMobile onAddDocumentMenuSelected={handleAddDocumentMenuSelect} />}
            </Styled.SideComponentContainer>}
            onBack={navigateBack}
        >
            <Styled.Wrapper>
                <Styled.DocumentFiltersForm close={closeFilters} displayFilters={displayFilters} />
            </Styled.Wrapper>
            <DocumentRoadSidebarContent />
            {!!linearLocationSelectorType && <DocumentLinearLocationSelector
                onClose={closeLinearLocationModal}
                onLinearLocationSelected={handleSelectLinearLocation}
                selectedLinearLocation={documentLocation}
                selectionType={linearLocationSelectorType}
            />}
            {documentAddModalType && <DocumentEditModal
                documentLocation={documentLocation}
                open={!!documentAddModalType}
                selectionType={documentAddModalType}
                onClose={() => {
                    setDocumentAddModalType(false)
                    setDocumentLocation(null)
                }}
                FormComponent={DocumentAddForm}
            />}
        </Styled.BottomSheetContent>
    )
}

const getReferentialRoad = (referential, currentRoad) => {
    if (!referential) {
        return []
    }

    return (
        referential.features
            .filter(road => road.linear_location[0].road === currentRoad)
            .reduce((acc, road) => {
                return ({
                    ...acc,
                    end: Math.max(acc.end, road.linear_location[0].end),
                    start: Math.min(acc.start, road.linear_location[0].start),
                    typeid: road.linear_location[0].typeid,
                })
            }, ({
                end: 0,
                road: currentRoad,
                start: Infinity,
                typeid: null,
            }))
    )
}